import { Autocomplete, Box, Checkbox, DialogContentText, FormControlLabel, Grid, IconButton, TextField,  useMediaQuery, Typography, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PropTypes from 'prop-types';
import { NumericFormat } from 'react-number-format';
import OrderSearchWithItemTable from './SpecialFieldTypes/OrderSearchWithItemTable';
import OrderSearchOnly from './SpecialFieldTypes/OrderSearchOnly';
import SimpleAutocompleteProps from '../../../../../reusable-components/autocomplete/SimpleAutocompleteProps';

TicketTypeFields.propTypes = {
  ticketTypeFieldGroups: PropTypes.array,
  setTicketTypeFieldGroups: PropTypes.func,
  facility: PropTypes.object,
};

export default function TicketTypeFields({ ticketTypeFieldGroups, setTicketTypeFieldGroups, facility })
{
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const setFieldValue = (originalField, group, inputValue) => {
    setTicketTypeFieldGroups(groups => {
      try {
        const updateGroup = groups.find(updateGroup => updateGroup.groupId === group.groupId);
        const updateField = updateGroup.fields.find(field => field.id === originalField.id);
        Object.keys(inputValue).forEach(key => { updateField[key] = inputValue[key]; });
        return [...groups];
      } catch {
        return [...groups];
      }
    });
  }

  const copyRepeatableGroup = (group) => {
    setTicketTypeFieldGroups(groups => {
      try {
        const repeatGroupIndex = groups.findIndex(repeatGroup => repeatGroup.groupId === group.groupId);
        if (repeatGroupIndex === -1) return groups;
        const repeatGroup = groups[repeatGroupIndex];
        if (!repeatGroup.repeatGroup) return [...groups];
        const repeatedGroup = groups.find(repeatedGroup => repeatedGroup.groupId === (repeatGroup.repeatedGroupId ?? repeatGroup.groupId));
        let newFieldId = groups.flatMap(group => group.fields.map(field => field.id)).reduce((prev, current) => (prev > current) ? prev : current, 0);
        let { displayHTMLValue } = repeatedGroup;
        const newFields = repeatedGroup.fields.map(field => ({ ...field, id: ++newFieldId, repeatedFieldId: field.id, value: null }));
        newFields.forEach(field => { displayHTMLValue = displayHTMLValue.replace(`{${field.repeatedFieldId} - ${field.name ?? ""}}`, `{${field.id} - ${field.name ?? ""}}`) }) 
        return groups.toSpliced(repeatGroupIndex + 1, 0, {
          ...repeatedGroup,
          headerGroup: false,
          repeatedGroup: true,
          displayHTMLValue,
          repeatedGroupId: repeatedGroup.groupId,
          groupId: (groups.reduce((prev, current) => (prev && prev.groupId > current.groupId) ? prev : current, null)?.groupId ?? 0) + 1,
          fields: newFields
        });
      } catch {
        return [...groups];
      }
    });
  }

  const repeatChains = ticketTypeFieldGroups.reduce((acc, group) => {
    if (!group.repeatGroup) return acc;
    const chainId = group.repeatedGroupId ?? group.groupId;
    const chain = acc[chainId] ?? { lastGroupId: -Infinity, count: 0 };
    acc[chainId] = { lastGroupId: Math.max(chain.lastGroupId, group.groupId), count: chain.count + 1 };
    return acc;
  }, {});

  const removeRow = (group) => {
    setTicketTypeFieldGroups(groups => {
      const remaining = groups.filter(g => g.groupId !== group.groupId);
      if (group.repeatedGroup) return remaining;
      const chainId = group.groupId;
      const nextInChain = remaining.find(g => g.repeatedGroupId === chainId);
      if (!nextInChain) return remaining;
      return remaining.map(g => {
        if (g.groupId === nextInChain.groupId) {
          const { repeatedGroupId, ...rest } = g;
          return { ...rest, repeatedGroup: false };
        }
        if (g.repeatedGroupId === chainId) return { ...g, repeatedGroupId: nextInChain.groupId };
        return g;
      });
    });
  }

  const displayField = (field, group) => {
    const defaultOpts = {
      size: "small",
      fullWidth: true,
      placeholder: field.name,
      autoComplete: "off"
    };

    switch (field.type) {
      case "Number":
        return <NumericFormat
          {...defaultOpts}
          value={field.value ?? ""}
          customInput={TextField}
          onValueChange={(values) => setFieldValue(field, group, { value: values?.value })}
        />;
      case "Currency":
        return <NumericFormat
          {...defaultOpts}
          customInput={TextField}
          value={field.value ?? ""}
          thousandSeparator
          size="small"
          fullWidth
          decimalScale={2}
          fixedDecimalScale
          prefix="$"
          onValueChange={(values) => setFieldValue(field, group, { value: values?.value })}
        />
      case "Checkbox":
        return <FormControlLabel control={<Checkbox checked={field.value === "Yes"} onChange={(event) => setFieldValue(field, group, { value: event.target.checked ? "Yes" : "No" })} />}
          label={field.name}
        />
      case "Dropdown":
        return <Autocomplete
          {...SimpleAutocompleteProps}
          options={field.dropdownOptions || []}
          value={field.dropdownOptions?.find(val => val.value === field.value) || null}
          onChange={(event, newValue) => setFieldValue(field, group, { value: newValue?.value ?? "" })}
          renderInput={(params) => <TextField
            {...params}
            type="search"
            label=""
            placeholder={field.name}
            variant="outlined"
            inputProps={{ ...params.inputProps, autoComplete: 'off' }}
            autoComplete="off"
          />}
        />
      case "Order Search":
        return <OrderSearchOnly field={field} facilityId={facility?.id ?? 0} setFieldValue={setFieldValue} group={group} />
      case "Order Search - With Item Table":
        return <OrderSearchWithItemTable field={field} facilityId={facility?.id ?? 0} setFieldValue={setFieldValue} group={group} />
      case "PS Item Number":
        return <NumericFormat {...defaultOpts} allowNegative={false} value={field.value ?? ""} customInput={TextField}
          onValueChange={(values) => setFieldValue(field, group, { value: values?.value })} />;
      case "Link": {
        const isValidLink = !field.value || /^(ftp|http|https):\/\/[^ "]+$/.test(field.value);
        return <TextField
          {...defaultOpts}
          value={field.value ?? ""}
          onChange={(e) => setFieldValue(field, group, { value: e.target.value })}
          error={!isValidLink}
          helperText={isValidLink ? "" : 'Please enter a valid link'}
          autoComplete="off"
        />;
      }
      default:
        return <TextField
          {...defaultOpts}
          value={field.value ?? ""}
          onChange={(e) => setFieldValue(field, group, { value: e.target.value })}
          autoComplete="off"
        />
    }
  }

  return ticketTypeFieldGroups.length > 0 ? <Box sx={{ px: '24px' }}>
    <Grid container direction="row" spacing={3}>
    {ticketTypeFieldGroups.filter((group) => group.fields?.some(field => field.name &&
      field.type && field.type !== "No Input Text")).map((group) => <Grid item key={group.groupId} xs={12}>
        <Grid container direction="row" spacing={3}>
          <Grid item xs={12}>
            <Stack direction="column" spacing={1}>
              {!group.miscellaneousFieldsGroup && !group.repeatedGroupId &&
                <DialogContentText>{group.groupLabel}</DialogContentText>}
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  ...(group.repeatGroup ? {
                    borderRadius: '12px',
                    border: '1px solid #EAEFEE',
                    bgcolor: '#FFF',
                    p: '24px',
                  } : {}),
                }}
              >
                <Grid
                  container
                  direction="row"
                  spacing={2}
                  sx={group.repeatGroup && repeatChains[group.repeatedGroupId ?? group.groupId]?.count > 1 ? { pr: 5 } : undefined}
                >
                  {group.fields.filter(field => field.name && field.type && field.type !== "No Input Text").map((field, index) =>
                    <Grid item key={index} xs={!isMobile && field.width > 0 ? 12 * (field.width / 100) : 12}>
                      {group.miscellaneousFieldsGroup && field.name && <DialogContentText>{field.name}</DialogContentText>}
                      {displayField(field, group)}
                    </Grid>)}
                </Grid>
                {group.repeatGroup && repeatChains[group.repeatedGroupId ?? group.groupId]?.count > 1 && <IconButton
                  onClick={() => removeRow(group)}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    right: 0,
                    transform: 'translateY(-50%)',
                    color: 'grey.500',
                    '&:hover': { color: 'error.main', bgcolor: (theme) => alpha(theme.palette.error.main, 0.08) },
                  }}
                >
                  <DeleteOutlineIcon />
                </IconButton>}
              </Box>
            </Stack>
          </Grid>
          {group.repeatGroup && repeatChains[group.repeatedGroupId ?? group.groupId]?.lastGroupId === group.groupId && <Grid item xs={12}>
            <Stack direction="row" sx={{ mt: 1.5 }} spacing={1.5}>
              <Stack
                direction="row"
                onClick={() => copyRepeatableGroup(group)}
                sx={{
                  height: 46,
                  px: '14px',
                  alignItems: 'center',
                  gap: 1,
                  flexGrow: 1,
                  justifyContent: 'center',
                  borderRadius: '8px',
                  border: '1px dashed',
                  borderColor: 'secondary.main',
                  bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.12),
                  cursor: 'pointer',
                }}
              >
                <AddIcon color="secondary" fontSize="small" />
                <Typography color="secondary">
                  Add another Row
                </Typography>
              </Stack>
            </Stack>
          </Grid>}
        </Grid>
      </Grid>)}
    </Grid>
  </Box> : "";
}